import {
  HsaConditionExpression,
  HsaConditionLeaf,
  HsaConditionLeafKind,
  HsaDateConditionOperator,
  hsaLeafKindForQuestionType,
  HsaMultiSelectConditionOperator,
  HsaNumericConditionOperator,
  HsaSingleSelectConditionOperator,
  HsaTextConditionOperator,
} from "@redotech/redo-conversion-hsa-common/hsa-condition";
import {
  getHsaPoolQuestion,
  isHsaQuestionHandle,
} from "@redotech/redo-conversion-hsa-common/hsa-question-pool";
import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import { t, useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { SegmentConjunctionSelect } from "@redotech/redo-web-marketing/marketing/segments/segment-conjunction-select";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { remove, replace } from "@redotech/util/array";
import { DeepPartial } from "@redotech/util/type";
import { memo, useMemo } from "react";
import {
  dateOperatorLabels,
  HsaQuestionLookup,
  multiSelectOperatorLabels,
  numericOperatorLabels,
  textOperatorLabels,
} from "../../nodes/hsa-condition-summary";
import {
  multiSelectLeafWithOperator,
  textLeafWithOperator,
} from "./hsa-condition-leaf";
import { HsaNumberField, HsaTextField } from "./hsa-inspector-inputs";

type PartialExpression = DeepPartial<HsaConditionExpression>;
type PartialBlock = NonNullable<
  NonNullable<PartialExpression["conditionBlocks"]>[number]
>;
type PartialLeaf = DeepPartial<HsaConditionLeaf>;

function singleSelectOperatorLabels(): Record<
  HsaSingleSelectConditionOperator,
  string
> {
  return {
    [HsaSingleSelectConditionOperator.IS_ANY_OF]: t("is any of"),
    [HsaSingleSelectConditionOperator.IS_NONE_OF]: t("is none of"),
  };
}

function enumOptions<T extends string>(
  values: T[],
  labels: Record<T, string>,
): RedoListItem<T>[] {
  return values.map((value) => ({
    id: value,
    type: "text",
    text: labels[value],
    value,
  }));
}

/** Fresh leaf for a newly selected question, with the kind's default operator. */
function leafForQuestion(handle: string): PartialLeaf {
  if (!isHsaQuestionHandle(handle)) {
    return { questionHandle: handle };
  }
  const kind = hsaLeafKindForQuestionType(getHsaPoolQuestion(handle).type);
  switch (kind) {
    case HsaConditionLeafKind.NUMERIC:
      return {
        questionHandle: handle,
        leafKind: kind,
        operator: HsaNumericConditionOperator.EQUALS,
      };
    case HsaConditionLeafKind.TEXT:
      return {
        questionHandle: handle,
        leafKind: kind,
        operator: HsaTextConditionOperator.EQUALS,
      };
    case HsaConditionLeafKind.DATE:
      return {
        questionHandle: handle,
        leafKind: kind,
        operator: HsaDateConditionOperator.AGE_AT_LEAST,
      };
    case HsaConditionLeafKind.SINGLE_SELECT:
      return {
        questionHandle: handle,
        leafKind: kind,
        operator: HsaSingleSelectConditionOperator.IS_ANY_OF,
      };
    case HsaConditionLeafKind.MULTI_SELECT:
      return {
        questionHandle: handle,
        leafKind: kind,
        operator: HsaMultiSelectConditionOperator.INCLUDES_ANY,
      };
  }
}

function questionOptions(
  availableHandles: readonly string[],
  lookup: HsaQuestionLookup,
): RedoListItem<string>[] {
  return availableHandles.map((handle) => ({
    id: handle,
    type: "text",
    text: lookup.get(handle)?.label ?? handle,
    supportingText: handle,
    value: handle,
  }));
}

const OptionValuesPicker = memo(function OptionValuesPicker({
  questionHandle,
  optionValues,
  setOptionValues,
}: {
  questionHandle: string | undefined;
  optionValues: readonly (string | undefined)[] | undefined;
  setOptionValues: (optionValues: string[]) => void;
}) {
  const { t } = useTranslation();
  const question =
    questionHandle && isHsaQuestionHandle(questionHandle)
      ? getHsaPoolQuestion(questionHandle)
      : undefined;
  const options =
    question && "options" in question ? (question.options ?? []) : [];
  const selected = useMemo(
    () =>
      new Set(
        (optionValues ?? []).filter(
          (value): value is string => typeof value === "string",
        ),
      ),
    [optionValues],
  );

  if (options.length === 0) {
    // Dynamic multi-select questions have no static options: values are
    // free-form (e.g. product ids), entered comma-separated.
    return (
      <HsaTextField
        label={t("Values (comma-separated)")}
        onCommit={(raw) =>
          setOptionValues(
            raw
              .split(",")
              .map((value) => value.trim())
              .filter(Boolean),
          )
        }
        value={[...selected].join(", ")}
      />
    );
  }

  return (
    <Flex dir="column" gap="xxs">
      {options.map((option) => (
        <RedoCheckbox
          key={option.value}
          label={option.label}
          setValue={(checked) => {
            const next = new Set(selected);
            if (checked === true) {
              next.add(option.value);
            } else {
              next.delete(option.value);
            }
            setOptionValues([...next]);
          }}
          size="sm"
          value={selected.has(option.value)}
        />
      ))}
    </Flex>
  );
});

const HsaLeafEditor = memo(function HsaLeafEditor({
  leaf,
  setLeaf,
  onRemove,
  availableHandles,
  lookup,
}: {
  leaf: PartialLeaf;
  setLeaf: (leaf: PartialLeaf) => void;
  onRemove: () => void;
  availableHandles: readonly string[];
  lookup: HsaQuestionLookup;
}) {
  const { t } = useTranslation();
  const questionItems = useMemo(() => {
    const items = questionOptions(availableHandles, lookup);
    // Keep the current selection visible even if it is no longer guaranteed
    // upstream — validation flags it rather than silently hiding it.
    if (
      leaf.questionHandle &&
      !items.some((item) => item.id === leaf.questionHandle)
    ) {
      items.push({
        id: leaf.questionHandle,
        type: "text",
        text: lookup.get(leaf.questionHandle)?.label ?? leaf.questionHandle,
        supportingText: t("No longer guaranteed answered before this step"),
        value: leaf.questionHandle,
      });
    }
    return items;
  }, [availableHandles, lookup, leaf.questionHandle, t]);

  const noQuestions = questionItems.length === 0;

  return (
    <Flex dir="column" gap="sm" w="full">
      <Flex align="center" gap="xs" w="full">
        <Flex grow={1} overflow="hidden">
          <RedoSingleSelectDropdownInput<string>
            disabled={noQuestions}
            fitToAnchor={false}
            options={questionItems}
            optionSelected={({ item }) => setLeaf(leafForQuestion(item.value))}
            placeholder={
              noQuestions
                ? t("Add a question group upstream first")
                : t("Select a question")
            }
            selectedItem={questionItems.find(
              (item) => item.id === leaf.questionHandle,
            )}
            size="xs"
          />
        </Flex>
        <RedoButton
          aria-label={t("Remove condition")}
          hierarchy="tertiary"
          IconLeading={TrashIcon}
          onClick={onRemove}
          size="xs"
        />
      </Flex>
      {leaf.questionHandle && (
        <HsaLeafOperatorAndValue leaf={leaf} setLeaf={setLeaf} />
      )}
    </Flex>
  );
});

const HsaLeafOperatorAndValue = memo(function HsaLeafOperatorAndValue({
  leaf,
  setLeaf,
}: {
  leaf: PartialLeaf;
  setLeaf: (leaf: PartialLeaf) => void;
}) {
  const { t } = useTranslation();
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (leaf.leafKind) {
    case HsaConditionLeafKind.NUMERIC:
      return (
        <Flex align="center" gap="xs" wrap="wrap">
          <RedoSingleSelectDropdownInput<HsaNumericConditionOperator>
            fitToAnchor={false}
            options={enumOptions(
              Object.values(HsaNumericConditionOperator),
              numericOperatorLabels(),
            )}
            optionSelected={({ item }) =>
              setLeaf({ ...leaf, operator: item.value })
            }
            selectedItem={enumOptions(
              Object.values(HsaNumericConditionOperator),
              numericOperatorLabels(),
            ).find((item) => item.id === leaf.operator)}
            size="xs"
          />
          <HsaNumberField
            onCommit={(value) => setLeaf({ ...leaf, value })}
            value={leaf.value}
          />
        </Flex>
      );
    case HsaConditionLeafKind.TEXT: {
      const takesValue =
        leaf.operator !== HsaTextConditionOperator.IS_EMPTY &&
        leaf.operator !== HsaTextConditionOperator.IS_NOT_EMPTY;
      return (
        <Flex align="center" gap="xs" wrap="wrap">
          <RedoSingleSelectDropdownInput<HsaTextConditionOperator>
            fitToAnchor={false}
            options={enumOptions(
              Object.values(HsaTextConditionOperator),
              textOperatorLabels(),
            )}
            optionSelected={({ item }) =>
              setLeaf(textLeafWithOperator(leaf, item.value))
            }
            selectedItem={enumOptions(
              Object.values(HsaTextConditionOperator),
              textOperatorLabels(),
            ).find((item) => item.id === leaf.operator)}
            size="xs"
          />
          {takesValue && (
            <HsaTextField
              label=""
              onCommit={(value) => setLeaf({ ...leaf, value })}
              value={leaf.value}
            />
          )}
        </Flex>
      );
    }
    case HsaConditionLeafKind.DATE:
      return (
        <Flex align="center" gap="xs" wrap="wrap">
          <RedoSingleSelectDropdownInput<HsaDateConditionOperator>
            fitToAnchor={false}
            options={enumOptions(
              Object.values(HsaDateConditionOperator),
              dateOperatorLabels(),
            )}
            optionSelected={({ item }) =>
              setLeaf({ ...leaf, operator: item.value })
            }
            selectedItem={enumOptions(
              Object.values(HsaDateConditionOperator),
              dateOperatorLabels(),
            ).find((item) => item.id === leaf.operator)}
            size="xs"
          />
          <HsaNumberField
            onCommit={(value) => setLeaf({ ...leaf, value })}
            placeholder={t("Years")}
            value={leaf.value}
          />
        </Flex>
      );
    case HsaConditionLeafKind.SINGLE_SELECT:
      return (
        <Flex dir="column" gap="xs">
          <RedoSingleSelectDropdownInput<HsaSingleSelectConditionOperator>
            fitToAnchor={false}
            options={enumOptions(
              Object.values(HsaSingleSelectConditionOperator),
              singleSelectOperatorLabels(),
            )}
            optionSelected={({ item }) =>
              setLeaf({ ...leaf, operator: item.value })
            }
            selectedItem={enumOptions(
              Object.values(HsaSingleSelectConditionOperator),
              singleSelectOperatorLabels(),
            ).find((item) => item.id === leaf.operator)}
            size="xs"
          />
          <OptionValuesPicker
            optionValues={leaf.optionValues}
            questionHandle={leaf.questionHandle}
            setOptionValues={(optionValues) =>
              setLeaf({ ...leaf, optionValues })
            }
          />
        </Flex>
      );
    case HsaConditionLeafKind.MULTI_SELECT: {
      const takesValues =
        leaf.operator !== HsaMultiSelectConditionOperator.IS_EMPTY &&
        leaf.operator !== HsaMultiSelectConditionOperator.IS_NOT_EMPTY;
      return (
        <Flex dir="column" gap="xs">
          <RedoSingleSelectDropdownInput<HsaMultiSelectConditionOperator>
            fitToAnchor={false}
            options={enumOptions(
              Object.values(HsaMultiSelectConditionOperator),
              multiSelectOperatorLabels(),
            )}
            optionSelected={({ item }) =>
              setLeaf(multiSelectLeafWithOperator(leaf, item.value))
            }
            selectedItem={enumOptions(
              Object.values(HsaMultiSelectConditionOperator),
              multiSelectOperatorLabels(),
            ).find((item) => item.id === leaf.operator)}
            size="xs"
          />
          {takesValues && (
            <OptionValuesPicker
              optionValues={leaf.optionValues}
              questionHandle={leaf.questionHandle}
              setOptionValues={(optionValues) =>
                setLeaf({ ...leaf, optionValues })
              }
            />
          )}
        </Flex>
      );
    }
    default:
      return null;
  }
});

export const HsaConditionExpressionEditor = memo(
  function HsaConditionExpressionEditor({
    expression,
    setExpression,
    availableHandles,
    lookup,
  }: {
    expression: PartialExpression | undefined;
    setExpression: (expression: PartialExpression) => void;
    availableHandles: readonly string[];
    lookup: HsaQuestionLookup;
  }) {
    const { t } = useTranslation();
    const conjunction = expression?.conjunction ?? ConjunctionMode.AND;
    const blocks: PartialBlock[] = (expression?.conditionBlocks ?? []).map(
      (block) => block ?? { conjunction: ConjunctionMode.AND, conditions: [] },
    );

    const commit = (
      nextBlocks: PartialBlock[],
      nextConjunction?: ConjunctionMode,
    ) =>
      setExpression({
        conjunction: nextConjunction ?? conjunction,
        conditionBlocks: nextBlocks,
      });

    return (
      <Flex dir="column" gap="lg">
        <SegmentConjunctionSelect
          mode={conjunction}
          setMode={(mode) => commit(blocks, mode)}
          textAfterConjunction={t("of these condition groups must match")}
        />
        {blocks.map((block, blockIndex) => {
          const leaves: PartialLeaf[] = (block.conditions ?? []).map(
            (leaf) => leaf ?? {},
          );
          const setBlock = (nextBlock: PartialBlock) =>
            commit(replace(blocks, blockIndex, nextBlock));
          return (
            <Flex
              bgColor="primary"
              borderColor="primary"
              borderRadius="lg"
              borderStyle="solid"
              borderWidth="1px"
              dir="column"
              gap="md"
              key={blockIndex}
              px="xl"
              py="lg"
              w="full"
            >
              <Flex align="center" justify="space-between">
                <SegmentConjunctionSelect
                  mode={block.conjunction ?? ConjunctionMode.AND}
                  setMode={(mode) => setBlock({ ...block, conjunction: mode })}
                  textAfterConjunction={t("of these conditions must match")}
                />
                <RedoButton
                  aria-label={t("Remove condition group")}
                  hierarchy="tertiary"
                  IconLeading={TrashIcon}
                  onClick={() => commit(remove(blocks, blockIndex))}
                  size="xs"
                />
              </Flex>
              {leaves.map((leaf, leafIndex) => (
                <HsaLeafEditor
                  availableHandles={availableHandles}
                  key={leafIndex}
                  leaf={leaf}
                  lookup={lookup}
                  onRemove={() =>
                    setBlock({
                      ...block,
                      conditions: remove(leaves, leafIndex),
                    })
                  }
                  setLeaf={(nextLeaf) =>
                    setBlock({
                      ...block,
                      conditions: replace(leaves, leafIndex, nextLeaf),
                    })
                  }
                />
              ))}
              <Flex>
                <RedoButton
                  hierarchy="secondary"
                  IconLeading={PlusIcon}
                  onClick={() =>
                    setBlock({ ...block, conditions: [...leaves, {}] })
                  }
                  size="xs"
                  text={t("Add condition")}
                />
              </Flex>
            </Flex>
          );
        })}
        {availableHandles.length === 0 && blocks.length === 0 && (
          <Text fontSize="xs" textColor="secondary">
            {t(
              "No questions are guaranteed answered before this step yet — add a question group upstream first.",
            )}
          </Text>
        )}
        <Flex>
          <RedoButton
            hierarchy="secondary"
            IconLeading={PlusIcon}
            onClick={() =>
              commit([
                ...blocks,
                { conjunction: ConjunctionMode.AND, conditions: [{}] },
              ])
            }
            size="xs"
            text={t("Add condition group")}
          />
        </Flex>
      </Flex>
    );
  },
);
